import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserPlus, Users, Shield, Trash2, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'intake_officer',
    tenant_ids: []
  });

  // Redirect if not super admin
  useEffect(() => {
    if (!isSuperAdmin()) {
      window.location.href = '/';
    }
  }, [isSuperAdmin]);

  // Load users and tenants
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, tenantsRes] = await Promise.all([
        axios.get(`${API}/auth/users`),
        axios.get(`${API}/tenants`)
      ]);
      setUsers(usersRes.data);
      setTenants(tenantsRes.data);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    setError(null);
    setSuccess(null);

    try {
      await axios.post(`${API}/auth/users`, newUser);
      setSuccess(`User ${newUser.email} created successfully!`);
      toast.success('User created successfully');
      
      // Reset form
      setNewUser({
        email: '',
        password: '',
        full_name: '',
        role: 'intake_officer',
        tenant_ids: []
      });
      
      // Reload users
      await loadData();
      
      // Close dialog after delay
      setTimeout(() => {
        setIsCreateDialogOpen(false);
        setSuccess(null);
      }, 2000);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to create user';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsCreating(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-500/10 text-purple-400 border-purple-500/30';
      case 'tenant_admin':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
      case 'intake_officer':
        return 'bg-teal-500/10 text-teal-400 border-teal-500/30';
      default:
        return 'bg-slate-500/10 text-slate-400 border-slate-500/30';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'tenant_admin':
        return 'Tenant Admin';
      case 'intake_officer':
        return 'Intake Officer';
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">User Management</h1>
            <p className="text-slate-400 mt-1">Manage user accounts and permissions</p>
          </div>
          
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-500 hover:bg-teal-600 text-white">
                <UserPlus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-white">Create New User</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Add a new user to the system
                </DialogDescription>
              </DialogHeader>

              {error && (
                <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="bg-green-500/10 border-green-500/20 text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Full Name</Label>
                    <Input
                      value={newUser.full_name}
                      onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Email</Label>
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="user@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Password</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                  />
                  <p className="text-xs text-slate-400">Must be at least 8 characters</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Role</Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="super_admin" className="text-white">
                        Super Admin - Full platform access
                      </SelectItem>
                      <SelectItem value="tenant_admin" className="text-white">
                        Tenant Admin - Manage assigned tenants
                      </SelectItem>
                      <SelectItem value="intake_officer" className="text-white">
                        Intake Officer - Create intake links
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {newUser.role !== 'super_admin' && (
                  <div className="space-y-2">
                    <Label className="text-white">Assign to Tenants</Label>
                    <div className="grid grid-cols-2 gap-2 p-4 bg-slate-800 rounded-lg border border-slate-700">
                      {tenants.map((tenant) => (
                        <label key={tenant.tenant_id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newUser.tenant_ids.includes(tenant.tenant_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewUser({
                                  ...newUser,
                                  tenant_ids: [...newUser.tenant_ids, tenant.tenant_id]
                                });
                              } else {
                                setNewUser({
                                  ...newUser,
                                  tenant_ids: newUser.tenant_ids.filter(id => id !== tenant.tenant_id)
                                });
                              }
                            }}
                            className="rounded border-slate-600"
                          />
                          <span className="text-sm text-white">{tenant.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {tenant.industry_type}
                          </Badge>
                        </label>
                      ))}
                    </div>
                    {newUser.tenant_ids.length === 0 && (
                      <p className="text-xs text-amber-400">⚠️ Select at least one tenant</p>
                    )}
                  </div>
                )}

                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCreateDialogOpen(false)}
                    className="border-slate-700 text-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isCreating || (newUser.role !== 'super_admin' && newUser.tenant_ids.length === 0)}
                    className="bg-teal-500 hover:bg-teal-600"
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create User'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Users</p>
                  <p className="text-3xl font-bold text-white mt-1">{users.length}</p>
                </div>
                <Users className="w-8 h-8 text-teal-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Super Admins</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {users.filter(u => u.role === 'super_admin').length}
                  </p>
                </div>
                <Shield className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Intake Officers</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {users.filter(u => u.role === 'intake_officer').length}
                  </p>
                </div>
                <Users className="w-8 h-8 text-teal-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users List */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">All Users</CardTitle>
            <CardDescription className="text-slate-400">
              Manage user accounts and permissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="p-4 bg-slate-800 rounded-lg border border-slate-700 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-white font-medium">{user.full_name}</h3>
                      <Badge variant="outline" className={getRoleBadgeColor(user.role)}>
                        {getRoleLabel(user.role)}
                      </Badge>
                      {user.id === currentUser?.id && (
                        <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
                          You
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-400 mt-1">{user.email}</p>
                    {user.tenants && user.tenants.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {user.tenants.map((tenant) => (
                          <Badge
                            key={tenant.id}
                            variant="outline"
                            className="text-xs border-teal-500/30 text-teal-400"
                          >
                            {tenant.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {user.role === 'super_admin' && (
                      <p className="text-xs text-purple-400 mt-2">✨ Access to all tenants</p>
                    )}
                  </div>

                  {/* Future: Add delete/edit buttons here */}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserManagement;
